/**
 * „Otevřít mapu výroby" — the one way out of a screen that just told the
 * admin something is not wired into the production map (KAN-1356).
 *
 * Six screens state the same fact in their own words — „chybí proces",
 * „není v matici uzlu", „Tato operace není v operation graphu", „Přidej
 * ji do uzlu" — and none of them said where that is done. The map is a
 * separate route the admin has to know to look for, which makes every
 * one of those sentences a riddle rather than a message.
 *
 * URL shape follows the former ``GateSettingsLink`` (KAN-239; removed
 * with the built-in Quality screens, KAN-1364), the first screen to
 * link into the map: ``lens=process`` picks the operation lens and
 * ``node`` preselects a card. An admin who lands on the hall lens
 * instead of the process one is being sent somewhere that does not
 * hold the setting the message was about.
 */
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { DEFAULT_LANG, isSupportedLang } from "@/i18n";

/** Map href for a language, optionally preselecting one node. */
export function productionMapHref(lang: string, nodeId?: string | null): string {
  const base = `/${lang}/admin/map?lens=process`;
  return nodeId ? `${base}&node=${encodeURIComponent(nodeId)}` : base;
}

export function ProductionMapLink({
  nodeId,
  label,
  className,
  testId = "production-map-link",
}: {
  /** Preselect this node. Omit to open the map itself. */
  nodeId?: string | null;
  /** Override the default wording when the sentence above needs it. */
  label?: string;
  className?: string;
  testId?: string;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  return (
    <Link
      to={productionMapHref(lang, nodeId)}
      className={className ?? "font-medium text-accent underline"}
      data-testid={testId}
    >
      {label ??
        (nodeId
          ? t("productionMapLink.openNode", {
              defaultValue: "Otevřít uzel v mapě výroby",
            })
          : t("productionMapLink.open", {
              defaultValue: "Otevřít mapu výroby",
            }))}
    </Link>
  );
}
